import { makeAutoObservable } from "mobx";
import type RootStore from "stores/RootStore";
import type { CommandPaletteItem, CommandCategory } from "views/accounting/types";

/**
 * CommandPalette Store
 * Manages command palette state and commands
 * Provides keyboard-first navigation (Ctrl+K / Cmd+K)
 */
class CommandPaletteStore {
  rootStore: RootStore;

  // ==================== State ====================

  isOpen = false;
  searchQuery = "";
  selectedIndex = 0;
  recentCommands: string[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Load recent commands from localStorage
    this.loadRecentCommands();
  }

  // ==================== Commands ====================

  get allCommands(): CommandPaletteItem[] {
    const routerStore = this.rootStore.routerStore;

    return [
      // Navigation
      {
        id: "nav-dashboard",
        label: "Go to Dashboard",
        shortcut: "Ctrl+D",
        category: "navigation",
        action: () => routerStore?.goTo("Accounting Dashboard"),
        keywords: ["home", "main", "overview"],
      },
      {
        id: "nav-gl",
        label: "Go to General Ledger",
        shortcut: "Ctrl+G",
        category: "navigation",
        action: () => routerStore?.goTo("General Ledger"),
        keywords: ["gl", "ledger", "transactions"],
      },
      {
        id: "nav-reconciliation",
        label: "Go to Reconciliation",
        shortcut: "Ctrl+R",
        category: "navigation",
        action: () => routerStore?.goTo("Account Reconciliation"),
        keywords: ["bank", "match", "reconcile"],
      },
      {
        id: "nav-invoices",
        label: "Go to Invoices",
        shortcut: "Ctrl+I",
        category: "navigation",
        action: () => routerStore?.goTo("Invoices"),
        keywords: ["ar", "receivable", "billing"],
      },
      {
        id: "nav-bills",
        label: "Go to Bills",
        shortcut: "Ctrl+B",
        category: "navigation",
        action: () => routerStore?.goTo("Accounts Payable"),
        keywords: ["ap", "payable", "vendor"],
      },
      {
        id: "nav-coa",
        label: "Go to Chart of Accounts",
        category: "navigation",
        action: () => routerStore?.goTo("Chart of Accounts"),
        keywords: ["accounts", "chart", "coa"],
      },

      // Create
      {
        id: "create-journal",
        label: "New Journal Entry",
        shortcut: "Ctrl+Shift+J",
        category: "create",
        action: () => {
          routerStore?.goTo("Journal Entries");
          routerStore?.openDialog({
            dialogName: "JournalEntryDialog",
            dialogContext: "create",
          });
        },
        keywords: ["je", "journal", "entry", "new"],
      },
      {
        id: "create-invoice",
        label: "New Invoice",
        shortcut: "Ctrl+Shift+I",
        category: "create",
        action: () => {
          routerStore?.goTo("Invoices");
          routerStore?.openDialog({
            dialogName: "InvoiceDialog",
            dialogContext: "create",
          });
        },
        keywords: ["invoice", "ar", "new", "bill"],
      },
      {
        id: "create-bill",
        label: "New Bill",
        shortcut: "Ctrl+Shift+B",
        category: "create",
        action: () => {
          routerStore?.goTo("Accounts Payable");
          routerStore?.openDialog({
            dialogName: "BillDialog",
            dialogContext: "create",
          });
        },
        keywords: ["bill", "ap", "new", "vendor"],
      },

      // Reports
      {
        id: "report-trial-balance",
        label: "Run Trial Balance",
        shortcut: "Ctrl+Shift+T",
        category: "reports",
        action: () => routerStore?.goTo("Trial Balance"),
        keywords: ["tb", "trial", "balance", "report"],
      },
      {
        id: "report-pl",
        label: "Run P&L Report",
        category: "reports",
        action: () => routerStore?.goTo("Income Statement"),
        keywords: ["profit", "loss", "income", "statement"],
      },
      {
        id: "report-balance-sheet",
        label: "Run Balance Sheet",
        category: "reports",
        action: () => routerStore?.goTo("Balance Sheet"),
        keywords: ["bs", "balance", "sheet", "assets", "liabilities"],
      },
      {
        id: "report-gl",
        label: "Run GL Report",
        category: "reports",
        action: () => {
          routerStore?.goTo("General Ledger");
          routerStore?.openDialog({
            dialogName: "ExportDialog",
            dialogContext: "export",
          });
        },
        keywords: ["gl", "general", "ledger", "report"],
      },

      // Search
      {
        id: "search-transactions",
        label: "Search Transactions",
        shortcut: "/",
        category: "search",
        action: () => routerStore?.goTo("General Ledger"),
        keywords: ["find", "search", "lookup", "transactions"],
      },
      {
        id: "search-invoices",
        label: "Search Invoices",
        category: "search",
        action: () => routerStore?.goTo("Invoices"),
        keywords: ["find", "search", "lookup", "invoices"],
      },

      // Settings
      {
        id: "settings-periods",
        label: "Manage Fiscal Periods",
        category: "settings",
        action: () => routerStore?.goTo("Fiscal Periods"),
        keywords: ["periods", "fiscal", "close", "lock"],
      },
    ];
  }

  // ==================== Computed ====================

  get filteredCommands(): CommandPaletteItem[] {
    if (!this.searchQuery) {
      // Show recent commands first, then all commands
      const recentItems = this.allCommands.filter((c) =>
        this.recentCommands.includes(c.id)
      );
      const otherItems = this.allCommands.filter(
        (c) => !this.recentCommands.includes(c.id)
      );
      return [...recentItems, ...otherItems];
    }

    const query = this.searchQuery.toLowerCase();
    return this.allCommands.filter((command) => {
      const labelMatch = command.label.toLowerCase().includes(query);
      const keywordMatch = command.keywords?.some((k) =>
        k.toLowerCase().includes(query)
      );
      return labelMatch || keywordMatch;
    });
  }

  get commandsByCategory(): Map<CommandCategory, CommandPaletteItem[]> {
    const map = new Map<CommandCategory, CommandPaletteItem[]>();
    for (const command of this.filteredCommands) {
      const existing = map.get(command.category) || [];
      map.set(command.category, [...existing, command]);
    }
    return map;
  }

  get selectedCommand(): CommandPaletteItem | null {
    return this.filteredCommands[this.selectedIndex] || null;
  }

  // ==================== Actions ====================

  open = () => {
    this.isOpen = true;
    this.searchQuery = "";
    this.selectedIndex = 0;
  };

  close = () => {
    this.isOpen = false;
    this.searchQuery = "";
    this.selectedIndex = 0;
  };

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
    this.selectedIndex = 0;
  };

  moveSelection = (direction: "up" | "down") => {
    const maxIndex = this.filteredCommands.length - 1;
    if (direction === "up") {
      this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : maxIndex;
    } else {
      this.selectedIndex = this.selectedIndex < maxIndex ? this.selectedIndex + 1 : 0;
    }
  };

  selectIndex = (index: number) => {
    this.selectedIndex = index;
  };

  executeSelected = () => {
    const command = this.selectedCommand;
    if (command) {
      this.executeCommand(command);
    }
  };

  executeCommand = (command: CommandPaletteItem) => {
    // Add to recent commands
    this.addToRecent(command.id);

    // Execute the command
    command.action();

    // Close the palette
    this.close();
  };

  // ==================== Recent Commands ====================

  private addToRecent(commandId: string) {
    // Remove if already exists
    this.recentCommands = this.recentCommands.filter((id) => id !== commandId);
    // Add to front
    this.recentCommands.unshift(commandId);
    // Keep only last 5
    this.recentCommands = this.recentCommands.slice(0, 5);
    // Save to localStorage
    this.saveRecentCommands();
  }

  private loadRecentCommands() {
    try {
      const stored = localStorage.getItem("commandPalette_recent");
      if (stored) {
        this.recentCommands = JSON.parse(stored);
      }
    } catch {
      this.recentCommands = [];
    }
  }

  private saveRecentCommands() {
    try {
      localStorage.setItem(
        "commandPalette_recent",
        JSON.stringify(this.recentCommands)
      );
    } catch {
      // Ignore storage errors
    }
  }
}

export default CommandPaletteStore;
